import { init, MapCanvasApi } from '@spiaggeit/map-canvas'
import { Color } from '@spiaggeit/spit-core'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { MapElementWithSectorId, BoundingBox2d } from '@/models/map.ts'
import { ServiceInfoPurchaseType } from '@/models/service.ts'
import { cartSlice } from '@/store/cartSlice/index.ts'
import {
  loadMapData,
  loadSectors,
  mapSelectors,
  mapSlice,
} from '@/store/mapSlice.ts'
import { servicesSlice } from '@/store/servicesSlice.ts'
import { toastSlice } from '@/store/toastSlice.ts'
import {
  MAP_CELL_SIZE,
  USER_HAS_VISITED_MAP_BEFORE_KEY,
} from '@/utils/constants.ts'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { normalizeReservation } from '@/utils/normalizeReservation'
import { normalizeSpot } from '@/utils/normalizeSpot'

function calculateInitialZoom(
  containerRect: { width: number; height: number },
  mapQuadrant: BoundingBox2d
) {
  const quadrantWidth = mapQuadrant.max.x - mapQuadrant.min.x
  const quadrantHeight = mapQuadrant.max.y - mapQuadrant.min.y
  const scaleX = containerRect.width / quadrantWidth
  const scaleY = containerRect.height / quadrantHeight
  return Math.min(scaleX, scaleY)
}

export const useMapUtils = () => {
  const canvasAPIRef = useRef<MapCanvasApi | null>(null)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const mapData = useAppSelector(mapSelectors.mapData)
  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const { t } = useTranslation()

  const [isCanvasReady, setIsCanvasReady] = useState(false)
  const dispatch = useAppDispatch()

  const handleMapElementClick = (elementId: string) => {
    if (!mapData?.elements) return

    const element = mapData.elements.find((element) => element.id == elementId)

    if (!element || !element.style) return
    const isAvailable = element.style.iconColor !== Color.GREY

    if (isAvailable) {
      dispatch(cartSlice.actions.setCartPopoverIsOpen(false))
      dispatch(mapSlice.actions.selectMapElement(element))
    } else {
      dispatch(
        toastSlice.actions.show({
          ctaCopy: t('common.messageUnderstood'),
          message: t('map.errors.unavailableMapSpot'),
        })
      )
    }
  }

  const resetCanvasHeightHandler = useDebounceCallback(() => {
    const firstChild = document.getElementById('canvas')
      ?.firstChild as HTMLDivElement

    if (!firstChild) return

    firstChild.style.height = ''
  }, 100)

  const availableSpots = useMemo(
    () => mapData?.elements?.filter((e) => e.style?.iconColor === Color.GREEN),
    [mapData]
  )

  const userHasVisitedMapBefore = localStorage.getItem(
    USER_HAS_VISITED_MAP_BEFORE_KEY
  )

  return {
    availableSpots: {
      areMoreThanOne: availableSpots && availableSpots.length > 1,
      areNone: availableSpots && availableSpots.length < 1,
      array: availableSpots,
      isOnlyOne: availableSpots && availableSpots.length === 1,
    },
    getCanvasAPI: () => {
      if (!canvasAPIRef.current) {
        throw new Error('Map canvas API is not available.')
      }

      return canvasAPIRef.current
    },
    highlightSelectedItems: function () {
      try {
        if (!mapData?.elements) {
          throw new Error('Map elements are not available.')
        }

        const api = this.getCanvasAPI()

        const isElementSelectedOrReserved = (
          element: MapElementWithSectorId
        ) => {
          // We need to compare name, sectorId and subType instead of map element id
          // since sometimes we have multiple map elements associated with a single spot/reservation
          // e.g.: sunbeds belonging to the same sunbed area, which need to be treated and booked as a single unit
          const isSelected = selectedElement
            ? getObjectsComparison(
              normalizeSpot(selectedElement),
              normalizeSpot(element)
            )
            : false

          const isReserved = cartReservations.some((reservation) =>
            getObjectsComparison(
              normalizeReservation(reservation),
              normalizeSpot(element)
            )
          )

          return isSelected || isReserved
        }

        mapData.elements.forEach((element) => {
          if (isElementSelectedOrReserved(element)) {
            api.highlightElement(element.id, Color.BLUE)
          } else {
            api.clearHighlight([element.id])
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    initMap: async function () {
      if (
        !mapData ||
        !mapData.elements ||
        !mapData.background ||
        !mapData.size
      ) {
        throw new Error('Required map data is not available.')
      }

      const mapSize = {
        height: mapData.size.height * MAP_CELL_SIZE,
        width: mapData.size.width * MAP_CELL_SIZE,
      }

      // Default to map center if for some reason we can't find the best quadrant
      // Happens when there are no available spots
      const initialPosition = {
        x: mapSize.width / 2,
        y: mapSize.height / 2,
      }

      const elements = [...(mapData.elements ?? [])]
      let initialZoom = undefined
      const container = document.getElementById('canvas')!
      const boundingClientRect = container.getBoundingClientRect()
      // If we identified the best quadrant, we override the initial map center position
      if (mapData.bestQuadrant != null) {
        initialPosition.x = mapData.bestQuadrant.center.x
        initialPosition.y = mapData.bestQuadrant.center.y
        initialZoom = calculateInitialZoom(
          boundingClientRect,
          mapData.bestQuadrant
        )
      }

      canvasAPIRef.current = await init({
        events: {
          onMapElementClick: handleMapElementClick,
        },
        opts: {
          assetsBaseUrl: `${import.meta.env.VITE_ASSETS_URL}/booking/map`,
          background: mapData.background,
          containerId: 'canvas',
          elements: elements,
          initialPosition: {
            x: -initialPosition.x,
            y: -initialPosition.y,
          },
          initialZoom: initialZoom,
          size: mapSize,
        },
      })

      if (mapData.bestQuadrant != null) {
        // Center canvas on position
        const stageInfo = canvasAPIRef.current.getStageInfo()
        initialPosition.x -= stageInfo.rect.width / 2
        initialPosition.y -= stageInfo.rect.height / 2
        canvasAPIRef.current.goToPosition({
          x: -initialPosition.x,
          y: -initialPosition.y,
        })
      }

      setIsCanvasReady(true)

      this.highlightSelectedItems()
    },
    isCanvasReady,
    loadInitialData: async function () {
      try {
        await dispatch(
          loadMapData({
            cellSize: MAP_CELL_SIZE,
            screenHeight: window?.innerHeight ?? 0,
            screenWidth: window?.innerWidth ?? 0,
          })
        ).unwrap()
        dispatch(loadSectors())
        dispatch(
          servicesSlice.actions.load({
            purchaseType: ServiceInfoPurchaseType.WITH_SPOT,
          })
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load data:', error)
      }
    },
    resetCanvasHeightHandler,
    userHasVisitedMapBefore,
    zoomIn: function () {
      canvasAPIRef.current?.zoomIn()
    },
    zoomOut: function () {
      canvasAPIRef.current?.zoomOut()
    },
  }
}
